/* ───────────────────────────────────────────────────────────────
   Agent Console - hover/focus tooltip (console v2, C1-5). Port of the
   approved prototype's components/Tooltip.tsx.

   Opens beside its trigger on the "end" side (right in LTR, left in RTL), on the
   "start" side (the mirror: left in LTR, right in RTL - for a trigger that sits
   against the inline-END edge of the window, like the contact panel, where "end"
   would be off-screen), or below it ("bottom"). Opens on hover or keyboard focus after a short delay;
   closes on leave, blur, click, Esc, and on a scroll that moves the trigger
   itself. Used so a collapsed icon rail / collapsed panel still tells you what
   an icon is.

   Positioning is `position: fixed` from the trigger's bounding box, with the
   physical side chosen by the trigger's reading direction - that is the one
   place a physical `left`/`right` is unavoidable, because getBoundingClientRect
   is physical. Everything else in the console uses logical properties.
   ─────────────────────────────────────────────────────────────── */
import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';

export type TooltipSide = 'end' | 'start' | 'bottom';

/** How long the pointer / focus has to rest before the tip opens. */
export const TOOLTIP_DELAY_MS = 250;

/**
 * Reading direction at `el`. Computed style first; the nearest explicit `dir` attribute is the fallback
 * because a style engine without a UA `dir` mapping (jsdom) reports `ltr` for a `dir="rtl"` wrapper. The
 * nearest explicit ltr/rtl wins, so an `rtl` page with an inner `dir="ltr"` island still resolves to ltr.
 */
function isRtl(el: HTMLElement): boolean {
  if (getComputedStyle(el).direction === 'rtl') return true;
  const holder = el.closest('[dir="rtl"],[dir="ltr"]');
  return holder?.getAttribute('dir') === 'rtl';
}

interface TipPos {
  top: number;
  left?: number;
  right?: number;
}

export function Tooltip({
  label,
  sub,
  children,
  side = 'end',
  block = false,
}: {
  label: string;
  /** Optional second line (e.g. the rule behind a folder). */
  sub?: string;
  children: ReactNode;
  side?: TooltipSide;
  /** Make the anchor a block box (a full-width rail item). Default is an inline box that hugs its trigger. */
  block?: boolean;
}) {
  const ref = useRef<HTMLSpanElement>(null);
  const timer = useRef<number | undefined>(undefined);
  const [pos, setPos] = useState<TipPos | null>(null);
  const id = useId();

  const show = useCallback(() => {
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const r = el.getBoundingClientRect();
      const rtl = isRtl(el);
      if (side === 'bottom') {
        setPos(rtl ? { top: r.bottom + 8, right: window.innerWidth - r.right } : { top: r.bottom + 8, left: r.left });
      } else {
        // 'end' runs away from the trigger toward the inline-end edge; 'start' is its mirror image. Physical
        // left/right is unavoidable here (getBoundingClientRect is physical), so the side is resolved per direction.
        const toRight = side === 'end' ? !rtl : rtl;
        setPos(
          toRight
            ? { top: r.top + r.height / 2, left: r.right + 10 }
            : { top: r.top + r.height / 2, right: window.innerWidth - r.left + 10 },
        );
      }
    }, TOOLTIP_DELAY_MS);
  }, [side]);

  const hide = useCallback(() => {
    window.clearTimeout(timer.current);
    setPos(null);
  }, []);

  useEffect(() => {
    if (!pos) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    // Only a scroll that moves the trigger itself should close it (the rail scrolling under it) - not the chat
    // timeline auto-scrolling elsewhere on the page. A scroll event's target is the element that scrolled;
    // `document` for the page, otherwise the scroller. It moved the trigger iff that target contains it.
    const onScroll = (e: Event) => {
      const target = e.target;
      if (target instanceof Node && ref.current && (target === document || target.contains(ref.current))) hide();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('scroll', onScroll, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', onScroll, true);
    };
  }, [pos, hide]);

  useEffect(() => () => window.clearTimeout(timer.current), []);

  return (
    <span
      ref={ref}
      className={block ? 'tip-anchor tip-block' : 'tip-anchor'}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onClick={hide}
      aria-describedby={pos ? id : undefined}
    >
      {children}
      {pos && (
        <span
          role="tooltip"
          id={id}
          className={`tip tip-${side}`}
          style={{ top: pos.top, left: pos.left, right: pos.right }}
        >
          <span className="tip-label">{label}</span>
          {sub && <span className="tip-sub">{sub}</span>}
        </span>
      )}
    </span>
  );
}
